package features.attendance.ui.modals

import kotlinx.html.*
import kotlinx.html.dom.append
import kotlinx.html.js.onClickFunction
import kotlinx.browser.document
import org.w3c.dom.HTMLElement
import shared.ui.ModalManager
import AppState
import features.attendance.helpers.AttendanceHelpers

object ClockInModal {
    fun render(root: HTMLElement) {
        root.append {
            div("modal-content fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-dark-800 border border-dark-700 rounded-2xl p-6 w-full max-w-md z-[101] hidden shadow-2xl scale-95 opacity-0 transition-all duration-300") {
                id = "clock-in-modal"
                
                // Determine if this is clock in or clock out
                val userId = AppState.currentUser?.id ?: ""
                val teamId = AppState.currentTeam?.id ?: ""
                val attendanceStatus = if (userId.isNotEmpty() && teamId.isNotEmpty()) {
                    AttendanceHelpers.getAttendanceStatus(userId, teamId)
                } else {
                    "not_clocked_in"
                }
                
                val isClockOut = attendanceStatus == "clocked_in"
                val modalTitle = if (isClockOut) "Clock Out" else "Clock In"
                val buttonText = if (isClockOut) "Confirm Clock Out" else "Confirm Clock In"
                val buttonColor = if (isClockOut) "bg-violet-500 hover:bg-violet-600" else "bg-brand-cyan hover:bg-brand-green"
                val emoji = if (isClockOut) "🏁" else "🚀"
                
                div("flex justify-between items-center mb-6") {
                    h3("text-xl font-bold") { +modalTitle }
                    button(classes = "text-slate-400 hover:text-white") {
                        onClickFunction = { ModalManager.close() }
                        +"✕"
                    }
                }
                
                div("space-y-4 mb-6") {
                    // Camera Section
                    div("bg-black rounded-xl overflow-hidden relative aspect-video flex items-center justify-center group") {
                        // Video Element for Real Camera
                        video {
                            id = "camera-feed"
                            classes = setOf("absolute", "inset-0", "w-full", "h-full", "object-cover")
                            attributes["autoplay"] = "true"
                            attributes["playsinline"] = "true"
                        }
                        
                        // Canvas for Capture (Hidden)
                        canvas {
                            id = "photo-canvas"
                            classes = setOf("hidden")
                        }
                        
                        // Placeholder / Loading State
                        div("absolute inset-0 flex items-center justify-center bg-dark-900 z-0") {
                            id = "camera-placeholder"
                            div("text-center") {
                                div("text-4xl mb-2 animate-bounce") { +"📸" }
                                p("text-slate-400 text-sm") { +"Starting Camera..." }
                            }
                        }
                        
                        // Captured Image Preview (Hidden initially)
                        img {
                            id = "captured-photo-preview"
                            classes = setOf("absolute", "inset-0", "w-full", "h-full", "object-cover", "hidden", "z-20")
                        }
                        
                        // Live indicator
                        div("absolute top-4 right-4 z-30 bg-red-500/80 backdrop-blur-md px-3 py-1 rounded-full flex items-center gap-2") {
                            span("w-2 h-2 bg-white rounded-full animate-pulse") {}
                            span("text-white text-xs font-bold") { +"LIVE" }
                        }
                    }
                    
                    // Location Section with Map
                    div("bg-dark-900 rounded-xl border border-dark-700 overflow-hidden") {
                        // Map Style Selector
                        div("p-2 bg-dark-800/50 border-b border-dark-700 flex items-center justify-between") {
                            span("text-xs text-slate-400") { +"Map Style:" }
                            select("bg-dark-700 text-white text-xs rounded px-2 py-1 border border-dark-600 focus:outline-none focus:border-brand-cyan") {
                                id = "map-style-selector"
                                attributes["onchange"] = "window.changeMapStyle(this.value)"
                                
                                option {
                                    value = "osm"
                                    selected = true
                                    +"OpenStreetMap"
                                }
                                option {
                                    value = "hot"
                                    +"OSM Humanitarian"
                                }
                                option {
                                    value = "positron"
                                    +"Light (Positron)"
                                }
                                option {
                                    value = "dark"
                                    +"Dark Matter"
                                }
                                option {
                                    value = "satellite"
                                    +"Satellite"
                                }
                                option {
                                    value = "terrain"
                                    +"Terrain"
                                }
                            }
                        }
                        
                        // Map Container
                        div("w-full h-48 relative") {
                            id = "location-map"
                            // Leaflet will render here
                        }
                        
                        // Location Info Bar
                        div("p-3 bg-dark-800/50 flex items-center justify-between") {
                            div("flex items-center gap-2") {
                                span("animate-pulse w-2 h-2 bg-emerald-500 rounded-full") { 
                                    id = "location-status-dot" 
                                }
                                div("text-xs") {
                                    div("text-white font-bold") { 
                                        id = "location-name"
                                        +"Tracking Location..." 
                                    }
                                    div("text-slate-400 font-mono") { 
                                        id = "location-coords"
                                        +"Waiting for GPS..." 
                                    }
                                }
                            }
                            span("text-xs text-slate-500") {
                                id = "location-accuracy"
                                +"Accuracy: --m"
                            }
                        }
                    }
                    
                    div {
                        label("block text-sm text-slate-400 mb-2") { +"Reason (Optional)" }
                        textArea(classes = "w-full bg-dark-900 border border-dark-700 rounded-xl p-3 focus:outline-none focus:border-brand-cyan text-white resize-none h-20") {
                            id = "clockin-reason-input"
                            placeholder = "Working on..."
                        }
                    }
                    
                    // Hidden inputs for form submission
                    input(type = InputType.hidden) { id = "clockin-photo-data" }
                    input(type = InputType.hidden) { id = "clockin-lat" }
                    input(type = InputType.hidden) { id = "clockin-lng" }
                }
                
                button(classes = "w-full py-3 rounded-xl $buttonColor text-white font-bold transition shadow-lg shadow-cyan-500/20 disabled:opacity-50 disabled:cursor-not-allowed") {
                    id = "clockin-submit-btn"
                    disabled = true // Disabled until location is ready
                    onClickFunction = { 
                        if (isClockOut) {
                            js("submitClockOut()")
                        } else {
                            js("submitClockIn()")
                        }
                    }
                    +"$emoji $buttonText"
                }
            }
        }
        
        // Inject Leaflet CSS and JS
        val leafletCSS = document.createElement("link") as org.w3c.dom.HTMLLinkElement
        leafletCSS.rel = "stylesheet"
        leafletCSS.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css"
        document.head?.appendChild(leafletCSS)
        
        val leafletJS = document.createElement("script") as org.w3c.dom.HTMLScriptElement
        leafletJS.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js"
        document.head?.appendChild(leafletJS)
        
        // Inject Camera & Location Scripts
        val script = document.createElement("script") as org.w3c.dom.HTMLScriptElement
        script.innerHTML = """
            window.cameraStream = null;
            window.locationMap = null;
            window.locationMarker = null;
            window.gpsWatchId = null;
            
            window.startCamera = async function() {
                try {
                    const video = document.getElementById('camera-feed');
                    if (!video) return;
                    
                    const stream = await navigator.mediaDevices.getUserMedia({ 
                        video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } }, 
                        audio: false 
                    });
                    
                    window.cameraStream = stream;
                    video.srcObject = stream;
                    video.onloadedmetadata = () => {
                        video.play();
                        document.getElementById('camera-placeholder').style.display = 'none';
                    };
                    console.log("📸 Camera started");
                } catch (err) {
                    console.error("📸 Camera error:", err);
                    const placeholder = document.getElementById('camera-placeholder');
                    if (placeholder) {
                        placeholder.innerHTML = '<div class="text-center"><div class="text-4xl mb-2">❌</div><p class="text-red-400 text-sm">Camera access denied</p></div>';
                    }
                }
            };
            
            window.stopCamera = function() {
                if (window.cameraStream) {
                    window.cameraStream.getTracks().forEach(track => track.stop());
                    window.cameraStream = null;
                    console.log("📸 Camera stopped");
                }
            };
            
            window.capturePhoto = function() {
                return new Promise((resolve, reject) => {
                    const video = document.getElementById('camera-feed');
                    const canvas = document.getElementById('photo-canvas');
                    const photoInput = document.getElementById('clockin-photo-data');
                    
                    if (video && canvas) {
                        // Resize to smaller resolution for smaller file size
                        canvas.width = 320;
                        canvas.height = 240;
                        const ctx = canvas.getContext('2d');
                        ctx.drawImage(video, 0, 0, 320, 240);
                        
                        // Get base64 data (JPEG 0.3 quality for very small size)
                        const dataUrl = canvas.toDataURL('image/jpeg', 0.3);
                        
                        // Update input
                        photoInput.value = dataUrl;
                        
                        console.log("📸 Photo captured (size: ~" + Math.round(dataUrl.length / 1024) + "KB)");
                        resolve(dataUrl);
                    } else {
                        reject(new Error("Camera not ready"));
                    }
                });
            };
            
            window.initMap = function() {
                // Wait for Leaflet to load
                if (typeof L === 'undefined') {
                    setTimeout(window.initMap, 100);
                    return;
                }
                
                const mapContainer = document.getElementById('location-map');
                if (!mapContainer || window.locationMap) return;
                
                // Initialize map centered on default location
                window.locationMap = L.map('location-map').setView([0, 0], 2);
                
                // Define tile layer options
                window.tileLayers = {
                    osm: L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                        attribution: '© OpenStreetMap',
                        maxZoom: 19
                    }),
                    hot: L.tileLayer('https://{s}.tile.openstreetmap.fr/hot/{z}/{x}/{y}.png', {
                        attribution: '© OpenStreetMap HOT',
                        maxZoom: 19
                    }),
                    positron: L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png', {
                        attribution: '© CartoDB',
                        maxZoom: 19
                    }),
                    dark: L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png', {
                        attribution: '© CartoDB',
                        maxZoom: 19
                    }),
                    satellite: L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
                        attribution: '© Esri',
                        maxZoom: 19
                    }),
                    terrain: L.tileLayer('https://stamen-tiles.a.ssl.fastly.net/terrain/{z}/{x}/{y}.jpg', {
                        attribution: '© Stamen Design',
                        maxZoom: 18
                    })
                };
                
                // Add default tile layer (OSM)
                window.currentTileLayer = window.tileLayers.osm;
                window.currentTileLayer.addTo(window.locationMap);
                
                console.log("🗺️ Map initialized with OSM tiles");
            };
            
            window.changeMapStyle = function(style) {
                if (!window.locationMap || !window.tileLayers[style]) return;
                
                // Remove current tile layer
                if (window.currentTileLayer) {
                    window.locationMap.removeLayer(window.currentTileLayer);
                }
                
                // Add new tile layer
                window.currentTileLayer = window.tileLayers[style];
                window.currentTileLayer.addTo(window.locationMap);
                
                console.log("🗺️ Map style changed to:", style);
            };
            
            window.updateMapLocation = function(lat, lng, accuracy) {
                if (!window.locationMap) return;
                
                // Update or create marker
                if (window.locationMarker) {
                    window.locationMarker.setLatLng([lat, lng]);
                } else {
                    window.locationMarker = L.marker([lat, lng], {
                        icon: L.divIcon({
                            className: 'custom-marker',
                            html: '<div style="background: #22d3ee; width: 20px; height: 20px; border-radius: 50%; border: 3px solid white; box-shadow: 0 0 10px rgba(34, 211, 238, 0.5);"></div>',
                            iconSize: [20, 20]
                        })
                    }).addTo(window.locationMap);
                }
                
                // Add accuracy circle
                if (window.accuracyCircle) {
                    window.accuracyCircle.setLatLng([lat, lng]);
                    window.accuracyCircle.setRadius(accuracy);
                } else {
                    window.accuracyCircle = L.circle([lat, lng], {
                        radius: accuracy,
                        color: '#22d3ee',
                        fillColor: '#22d3ee',
                        fillOpacity: 0.1,
                        weight: 1
                    }).addTo(window.locationMap);
                }
                
                // Center map on location
                window.locationMap.setView([lat, lng], 16);
            };
            
            window.startGPSTracking = function() {
                const nameEl = document.getElementById('location-name');
                const coordsEl = document.getElementById('location-coords');
                const dotEl = document.getElementById('location-status-dot');
                const accuracyEl = document.getElementById('location-accuracy');
                const latInput = document.getElementById('clockin-lat');
                const lngInput = document.getElementById('clockin-lng');
                
                if (!nameEl || !coordsEl) return;
                
                if (!navigator.geolocation) {
                    coordsEl.textContent = "Geolocation not supported";
                    if (dotEl) dotEl.className = "w-2 h-2 bg-red-500 rounded-full";
                    return;
                }
                
                let locationFound = false;
                
                // Try high accuracy first with longer timeout
                window.gpsWatchId = navigator.geolocation.watchPosition(
                    (position) => {
                        locationFound = true;
                        const lat = position.coords.latitude;
                        const lng = position.coords.longitude;
                        const acc = Math.round(position.coords.accuracy);
                        
                        nameEl.textContent = "Live Location";
                        coordsEl.textContent = lat.toFixed(6) + ", " + lng.toFixed(6);
                        if (dotEl) dotEl.className = "w-2 h-2 bg-emerald-500 rounded-full animate-pulse";
                        if (accuracyEl) accuracyEl.textContent = "Accuracy: ±" + acc + "m";
                        
                        latInput.value = lat;
                        lngInput.value = lng;
                        
                        // Update map
                        window.updateMapLocation(lat, lng, acc);
                        
                        checkReadyToSubmit();
                    },
                    (error) => {
                        console.warn("📍 High-accuracy GPS error:", error);
                        
                        // If no location found yet, try low accuracy as fallback
                        if (!locationFound) {
                            nameEl.textContent = "Trying low-accuracy mode...";
                            coordsEl.textContent = "Please wait...";
                            if (dotEl) dotEl.className = "w-2 h-2 bg-yellow-500 rounded-full animate-pulse";
                            
                            // Stop high accuracy watch
                            if (window.gpsWatchId) {
                                navigator.geolocation.clearWatch(window.gpsWatchId);
                            }
                            
                            // Try low accuracy mode
                            window.gpsWatchId = navigator.geolocation.watchPosition(
                                (position) => {
                                    locationFound = true;
                                    const lat = position.coords.latitude;
                                    const lng = position.coords.longitude;
                                    const acc = Math.round(position.coords.accuracy);
                                    
                                    nameEl.textContent = "Approximate Location";
                                    coordsEl.textContent = lat.toFixed(6) + ", " + lng.toFixed(6);
                                    if (dotEl) dotEl.className = "w-2 h-2 bg-yellow-500 rounded-full animate-pulse";
                                    if (accuracyEl) accuracyEl.textContent = "Accuracy: ±" + acc + "m (low)";
                                    
                                    latInput.value = lat;
                                    lngInput.value = lng;
                                    
                                    window.updateMapLocation(lat, lng, acc);
                                    checkReadyToSubmit();
                                },
                                (fallbackError) => {
                                    console.error("📍 Low-accuracy GPS also failed:", fallbackError);
                                    nameEl.textContent = "Location Unavailable";
                                    coordsEl.textContent = "GPS timeout - Try moving to window or outside";
                                    if (dotEl) dotEl.className = "w-2 h-2 bg-red-500 rounded-full";
                                },
                                { 
                                    enableHighAccuracy: false, 
                                    timeout: 30000, 
                                    maximumAge: 60000 
                                }
                            );
                        }
                    },
                    { 
                        enableHighAccuracy: true, 
                        timeout: 60000, 
                        maximumAge: 0 
                    }
                );
                
                console.log("📍 GPS tracking started (60s timeout)");
            };
            
            window.stopGPSTracking = function() {
                if (window.gpsWatchId) {
                    navigator.geolocation.clearWatch(window.gpsWatchId);
                    window.gpsWatchId = null;
                    console.log("📍 GPS tracking stopped");
                }
            };
            
            window.checkReadyToSubmit = function() {
                const hasLocation = document.getElementById('clockin-lat').value !== '';
                const btn = document.getElementById('clockin-submit-btn');
                
                if (hasLocation) {
                    btn.disabled = false;
                    btn.classList.remove('opacity-50', 'cursor-not-allowed');
                } else {
                    btn.disabled = true;
                    btn.classList.add('opacity-50', 'cursor-not-allowed');
                }
            };
            
            // Setup MutationObserver to watch for modal visibility
            const modalObserver = new MutationObserver((mutations) => {
                mutations.forEach((mutation) => {
                    if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
                        const modal = document.getElementById('clock-in-modal');
                        if (modal && !modal.classList.contains('hidden')) {
                            // Modal just opened - detect current attendance status
                            console.log("🎬 Clock In Modal opened - detecting attendance status");
                            
                            // Get current attendance status
                            const userId = window.AppState?.currentUser?.id || '';
                            const teamId = window.AppState?.currentTeam?.id || '';
                            
                            if (userId && teamId) {
                                // Call Kotlin function to get attendance status
                                const attendanceStatus = window.getAttendanceStatusForModal(userId, teamId);
                                const isClockOut = attendanceStatus === 'clocked_in';
                                
                                console.log("📊 Attendance status:", attendanceStatus, "| isClockOut:", isClockOut);
                                
                                // Update modal title
                                const titleEl = modal.querySelector('h3');
                                if (titleEl) {
                                    titleEl.textContent = isClockOut ? 'Clock Out' : 'Clock In';
                                }
                                
                                // Update emoji
                                const emojiEl = modal.querySelector('.text-4xl.mb-4');
                                if (emojiEl) {
                                    emojiEl.textContent = isClockOut ? '🏁' : '🚀';
                                }
                                
                                // Update submit button
                                const submitBtn = document.getElementById('clockin-submit-btn');
                                if (submitBtn) {
                                    submitBtn.textContent = isClockOut ? 'Confirm Clock Out' : 'Confirm Clock In';
                                    submitBtn.className = isClockOut 
                                        ? 'w-full bg-violet-500 hover:bg-violet-600 text-white font-bold py-3 rounded-xl transition flex items-center justify-center gap-2'
                                        : 'w-full bg-brand-cyan hover:bg-brand-green text-white font-bold py-3 rounded-xl transition flex items-center justify-center gap-2';
                                }
                            }
                            
                            // Only start camera/GPS if not already running
                            setTimeout(() => {
                                if (!window.cameraStream) {
                                    window.startCamera();
                                }
                                if (!window.locationMap) {
                                    window.initMap();
                                }
                                if (!window.gpsWatchId) {
                                    window.startGPSTracking();
                                }
                            }, 100);
                        } else if (modal && modal.classList.contains('hidden')) {
                            // Modal just closed
                            console.log("🛑 Clock In Modal closed - stopping camera and GPS");
                            window.stopCamera();
                            window.stopGPSTracking();
                            
                            // Reset form
                            const photoInput = document.getElementById('clockin-photo-data');
                            const latInput = document.getElementById('clockin-lat');
                            const lngInput = document.getElementById('clockin-lng');
                            
                            if (photoInput) photoInput.value = '';
                            if (latInput) latInput.value = '';
                            if (lngInput) lngInput.value = '';
                            
                            const placeholder = document.getElementById('camera-placeholder');
                            if (placeholder) {
                                placeholder.style.display = 'flex';
                                placeholder.innerHTML = '<div class="text-center"><div class="text-4xl mb-2 animate-bounce">📸</div><p class="text-slate-400 text-sm">Starting Camera...</p></div>';
                            }
                            
                            // Destroy map
                            if (window.locationMap) {
                                window.locationMap.remove();
                                window.locationMap = null;
                                window.locationMarker = null;
                                window.accuracyCircle = null;
                            }
                        }
                    }
                });
            });
            
            // Start observing the modal element
            const observeModal = () => {
                const modal = document.getElementById('clock-in-modal');
                if (modal) {
                    modalObserver.observe(modal, { attributes: true, attributeFilter: ['class'] });
                    console.log("👀 MutationObserver attached to clock-in-modal");
                } else {
                    // Retry if modal not found yet
                    setTimeout(observeModal, 500);
                }
            };
            
            observeModal();
        """
        document.body?.appendChild(script)
    }
}
